import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { phoneDriverPath } from '@lib/routes';
import type { BoardDay } from '../board/boardModel';
import type { DeliveryFlowWire } from '../board/boardWire';
import { summarizeRouteEta } from '../board/etaSignals';
import { EXECUTOR_LABEL, flowProvider } from '../board/flowCopy';
import { useFocusCards } from '../board/useFocusCards';
import { useEnRouteSummary } from '../hooks/useEnRouteSummary';
import { useRouteEta } from '../hooks/useRouteEta';
import { buildEnRouteRows, enRouteTimes } from '../panes/enRouteModel';
import { StopDots } from '../panes/StopDots';
import { phoneHeadline, pickHero, sortPhoneRows } from './phoneModel';

const DASH = '—';
const HEADLINE_CLASS = 'm-0 text-[22px] leading-[1.3] font-semibold tracking-[-0.02em]';
const ROW_CLASS =
  'grid min-h-11 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1 border-t border-border/50 px-3.5 py-[11px]';

interface PhoneNowProps {
  today: string;
  flow: UseQueryResult<DeliveryFlowWire>;
  board: UseQueryResult<BoardDay[]>;
  row: BoardDay | undefined;
}

/**
 * The phone "now" view (#2487 N1-N8): the verdict headline and the reminder
 * card from S3's focus cards, then today's drivers from the S7b row model.
 * Every read is the desktop's (same hook, same query key); ETA and en-route
 * only while today has jobs on the road. Read-only: a driver row and the late
 * card open that driver's list (#2677); a phone has no outside link.
 */
export function PhoneNow({ today, flow, board, row }: PhoneNowProps) {
  const { t, i18n } = useTranslation();
  const nowMs = useMinuteNow();
  const enRouteOn = (row?.counts.en_route ?? 0) > 0;
  const eta = useRouteEta(enRouteOn);
  const times = flow.data ? enRouteTimes(flow.data, today) : null;
  const windowEndMs = times?.windowEndMs ?? null;
  const etaSummary =
    eta.data && windowEndMs !== null ? summarizeRouteEta(eta.data, windowEndMs) : undefined;
  const focus = useFocusCards({
    today,
    days: board.data ?? [],
    flow,
    eta: etaSummary,
    nowMs,
    enabled: true,
  });
  const enRoute = useEnRouteSummary(today, true, { enabled: enRouteOn });
  const rows = enRoute.data ? buildEnRouteRows(enRoute.data, eta.data, windowEndMs) : [];

  const executor = flow.data ? flowProvider(flow.data) : null;
  const provider = executor === null ? null : t(EXECUTOR_LABEL[executor]);
  const clock = (ms: number) =>
    new Intl.DateTimeFormat(i18n.language, {
      timeZone: flow.data?.timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(ms));

  let headline: ReturnType<typeof phoneHeadline> | null = null;
  if (board.isSuccess) {
    // The cards before the cap: the progress card leads the headline even
    // when the desktop row gives its place to an exception (#2487 N1).
    headline = phoneHeadline(
      focus.held,
      etaSummary && times?.windowEnd
        ? { count: etaSummary.late, windowEnd: times.windowEnd }
        : null,
      focus,
    );
  } else if (board.isError && !board.data) {
    // Nothing to read the day from: say the read failed, as S3 does.
    headline = { parts: [{ key: 'error.loadFailed', params: {} }], failed: true };
  }

  const hero = pickHero(focus.cards);
  const sortedRows = sortPhoneRows(rows);
  // Latest ETA first: if the first row is not late, no row is.
  const heroTarget = sortedRows[0];
  const heroDriver =
    hero?.kind === 'late' && heroTarget?.late === true && heroTarget.workerId !== null
      ? { workerId: heroTarget.workerId, name: heroTarget.driverName }
      : null;
  const lateRows = rows.filter((r) => r.late);
  const stopsLeft: number = lateRows.reduce((sum, r) => sum + r.enRoute, 0);
  let heroSub: string | null = null;
  if (hero?.kind === 'late' && lateRows.length > 0 && provider !== null) {
    heroSub = t('dispatch.phone.hero.lateSub', { provider, count: stopsLeft });
  } else if (hero?.sub) {
    heroSub = t(hero.sub.key, hero.sub.params);
  }

  return (
    <div data-testid="phone-now" className="flex flex-col gap-3">
      {headline && (
        <h2 className={headline.failed ? `${HEADLINE_CLASS} text-destructive` : HEADLINE_CLASS}>
          {headline.parts.map((part) => t(part.key, part.params)).join('')}
        </h2>
      )}

      {hero && (
        <div
          data-testid="phone-hero"
          className={`flex flex-col gap-2.5 rounded-xl p-3.5 ${
            hero.tone === 'block' ? 'bg-destructive/12' : 'bg-warning/12'
          }`}
        >
          <div>
            <b className="font-bold">{t(hero.title.key, hero.title.params)}</b>
            {heroSub && <span className="block text-[13px] text-muted-foreground">{heroSub}</span>}
          </div>
          {heroDriver && (
            <Link
              to={phoneDriverPath(heroDriver.workerId)}
              className="inline-flex h-11 w-full items-center justify-center rounded-lg border border-border bg-card px-3.5 font-medium text-foreground no-underline"
            >
              {t('dispatch.phone.hero.viewDriverStops', {
                name: heroDriver.name ?? t('dispatch.pane.enRoute.unknownDriver'),
              })}
            </Link>
          )}
        </div>
      )}

      {enRouteOn && rows.length > 0 && (
        <div
          data-testid="phone-drivers"
          className="rounded-xl bg-card outline outline-1 -outline-offset-1 outline-border/50"
        >
          <div className="flex px-3.5 pt-3 pb-0.5 font-semibold">
            {t('dispatch.phone.drivers.title')}
            <span className="ml-auto text-[12.5px] font-normal text-muted-foreground">
              {t('dispatch.phone.drivers.legend', { time: times?.windowEnd ?? DASH })}
            </span>
          </div>
          {sortedRows.map((r) => {
            const body: ReactNode = (
              <>
                <span className="min-w-0 truncate">
                  <span className="font-medium">
                    {r.driverName ?? t('dispatch.pane.enRoute.unknownDriver')}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">
                    {` · ${r.completed}/${r.completed + r.enRoute}`}
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  {r.etaMs === null ? (
                    <span className="font-mono text-[12.5px] text-muted-foreground">{DASH}</span>
                  ) : r.late ? (
                    <span
                      data-late="true"
                      className="rounded-sm bg-warning/10 px-2 text-[12px] leading-[22px] font-medium whitespace-nowrap text-foreground"
                    >
                      {clock(r.etaMs)}
                    </span>
                  ) : (
                    <span className="font-mono text-[12.5px]">{clock(r.etaMs)}</span>
                  )}
                  {r.workerId !== null && (
                    <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
                  )}
                </span>
                <span className="col-span-2 grid min-w-0">
                  <StopDots row={r} />
                </span>
              </>
            );
            // A row without a worker id cannot be found in the ETA report, so it opens nothing.
            return r.workerId === null ? (
              <div key={r.key} data-testid={`phone-driver-${r.key}`} className={ROW_CLASS}>
                {body}
              </div>
            ) : (
              <Link
                key={r.key}
                to={phoneDriverPath(r.workerId)}
                data-testid={`phone-driver-${r.key}`}
                className={`${ROW_CLASS} text-foreground no-underline`}
              >
                {body}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
